import React, {useCallback, useEffect, useReducer, useRef, useState} from "react";
import {Button, Col, Modal, Row} from "react-bootstrap";
import Swal from "sweetalert2";
import {DataGrid, DateRangeBox, LoadPanel, Tooltip} from "devextreme-react";
import {useNavigate} from "react-router-dom";
import {Column, ColumnChooser, Grouping, GroupPanel, Pager, Paging, SearchPanel} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import {IListViewField} from "./ILIstViewField";
import {toast} from "react-toastify";
import {IPostDocumentResponseDto} from "../../dtos/documents/IPostDocumentResponseDto";
import {addMessage} from "../../redux/slices/userMessageSlice";
import {useDispatch, useSelector} from "react-redux";
import {NewDocumentModalView} from "./NewDocumentModalView";
import SuccessIcon from "../../assets/img/svg/SuccessIcon";
import {IListViewCreateMoreActions} from "./IListViewCreateMoreAction";
import {RootState} from "../../redux/store/store";
import {setIsDetailViewOpen} from "../../redux/slices/modalAsTabSlice";
import {v4 as uuidv4} from "uuid";

import {useAppDispatch, useAppSelector} from "../../redux/hooks/hooks";
import {selectGridUpdateTrigger, setGridUpdateTrigger} from "../../redux/slices/gridSlice";
import {TransactionService} from "../../services/transactions/TransactionService";
import TransactionsPage from "../Pages/Transactions/TransactionsPage";
import {useTranslation} from "react-i18next";
import {Popup} from "devextreme-react/popup";
import {NFLoadPanel} from "./NFLoadPanel";
import {ITab, addTab} from "../../redux/slices/newTabSlice";
import {format} from "date-fns";
import NewDocumentListViewToolbar from "./NewDocumentListViewToolbar";
import {IListViewCreateDependedDocumentAction} from "./IListViewCreateDependedDocumentAction";
import {IAppEntity} from "../../config/entities";
import DocumentsHierarchyPage from "../Pages/DocumentsHierarchy/DocumentsHierarchyPage";

export interface IListViewOptions {
    paginationEnabled?: boolean;
}

interface IProps {
    fields: IListViewField[];
    createDocumentActions?: IListViewCreateDependedDocumentAction[];
    entity: any;
    children?: any;
    createMoreActions?: IListViewCreateMoreActions[];
    listViewOptions?: IListViewOptions;
    toolbarOptions?: {
        hidePostButton: boolean;
        hideCloneButton: boolean;
        hideTransactionButton: boolean;
    };
}

export const NewDocumentListView = ({
                                        toolbarOptions,
                                        children,
                                        fields,
                                        entity,
                                        createDocumentActions,
                                        createMoreActions,
                                        listViewOptions
                                    }: IProps) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const pageSizes = [10, 25, 50, 100];
    const dataGridRef = useRef<DataGrid | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [showDateRangeBox, setShowDateRangeBox] = useState<boolean>(false);

    const [createDocumentActionVisible, setCreateDocumentActionVisible] = useState<boolean>(false);
    const [createMoreActionsVisible, setCreateMoreActionsVisible] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);
    const [isFailure, setIsFailure] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isDetailViewCreateDocumentOpen, setIsDetailViewCreateDocumentOpen] = useState<boolean>(false);
    const [gridData, setGridData] = useState<any[]>([]);
    const [createDocumentEntity, setCreateDocumentEntity] = useState<IAppEntity | null>(null);
    const uniqueId = uuidv4();
    const isDetailViewOpen = useSelector((state: RootState) => state.modalsOpen.isDetailViewOpen);
    const [selectedItem, setSelectedItem] = useState<any | undefined>(undefined);

    const [isDetailViewCreateBaseAtDocumentOpen, setIsDetailViewCreateBaseAtDocumentOpen] = useState<boolean>(false);
    const [createBaseAtDocumentEntity, setCreateBaseAtDocumentEntity] = useState<IAppEntity | null>(null);

    const [clonedObject, setClonedObject] = useState<any>(null);
    const [baseObject, setBaseObject] = useState<any>(null);
    const activeTabId = useAppSelector((state: any) => state.newTab.activeTab);
    // const leftTabs = useAppSelector((state: any) => state.tabs.leftTabs);
    // const rightTabs = useAppSelector((state: any) => state.tabs.rightTabs);
    const tabs = useAppSelector((state: any) => state.newTab.tabs);
    // const direction = useAppSelector((state: any) => state.tabs.direction);
    const dispatch = useAppDispatch();
    const [updatedData, setUpdatedData] = useState<string>();
    const renderType = useAppSelector((state) => state.newTab.renderType);
    // const customDataSource = new CustomStore({
    //     key: "id",
    //     loadMode: "processed",
    //     cacheRawData: true,
        //     load: async (options) => {
    //         try {
    //             setIsLoading(true);
    //             const dataService = entity.getDataService();
    //             if (listViewOptions?.paginationEnabled === true) {
    //                 const response = await dataService.GetPage(
    //                     options.take,
    //                     options.skip,
    //                     undefined
    //                 );
    //                 console.log("response", response);
    //                 const respObject = {
    //                     data: response.items,
    //                     totalCount: response.count,
    //                 };
    //                 console.log("data", respObject);
    //                 return respObject;
    //             } else {
    //                 return await dataService.GetAll();
    //             }
    //         } catch (e: any) {
    //             console.log(e);
    //             toast.error(e.message, {
    //                 icon: <ErrorIcon/>,
    //                 closeButton: <CloseIcon/>,
    //             });
    //
    //         } finally {
    //             setIsLoading(false);
    //         }
    //     },
    // });

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const dataService = entity.getDataService();
            const data = await dataService.GetAll(searchText, startDate, endDate);
            setGridData(data);
        } catch (e: any) {
            console.log(e);
            toast.error(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartDateChange = (selectedStartDate: Date) => {
        setStartDate(selectedStartDate);
        // console.log("Start Date Selected:", selectedStartDate);
    };

    const handleEndDateChange = (selectedEndDate: Date) => {
        setEndDate(selectedEndDate);
        // console.log("End Date Selected:", selectedEndDate);
    };

    const handleCloseDataRangeModal = () => {
        setShowDateRangeBox(false);
    };

    const RefreshGridData = async () => {
        if (showDateRangeBox) setShowDateRangeBox(false);

        await fetchData();
    };

    const gridUpdateTrigger = useAppSelector(selectGridUpdateTrigger);
    const handleComponentRefresh = () => {
        dispatch(setGridUpdateTrigger());
    };

    const handleEditClick = async () => {
        if (!selectedItem) return;

        try {
            setIsDetailLoading(true);
            setClonedObject(undefined);
            dispatch(setIsDetailViewOpen(true));
            const id = uuidv4();
            const dataService = entity.getDataService();
            const item = await dataService.Get(selectedItem.id);
            const newTab: ITab = {
                id,
                title: "",
                entity,
                side: "left",
                isModified: false,
                parentTabId: activeTabId,
                // isChild: true,
                // isActive: true,
                // tabTitle: updatedData,
                documentNumber: item.documentNumber,
                documentDate: new Date(item.documentDate).toLocaleString().slice(0, 10),
                documentStatus: item.status,
                component: (
                    <NewDocumentModalView
                        entity={entity}
                        onClose={(event) => {
                            handleCloseDetailView(true);
                        }}
                        id={selectedItem.id}
                        clonedObject={clonedObject}
                        setUpdatedData={setUpdatedData}
                    />
                )
            };
            dispatch(addTab(newTab));
        } catch (e: any) {
            setIsFailure(true);
            setErrorMessage(e.message);
            toast.error(e.message);
        } finally {
            setIsDetailLoading(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!selectedItem) {
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            const dataService = entity.getDataService();
            await dataService.Delete(selectedItem.id);
            setSelectedItem(undefined);
            dispatch(setGridUpdateTrigger());
            await Swal.fire("Deleted!", "Your file has been deleted.", "success");
        }
    };

    // const handleRemoveTab = (id: string) => {
    //   const tabIndex = leftTabs.findIndex((tab: any) => tab.id === id);
    //   const nextTabId =
    //     tabIndex < leftTabs.length - 1
    //       ? leftTabs[tabIndex + 1].id
    //       : leftTabs[0].id;
    //   dispatch(removeTab(id));
    //   dispatch(setActiveTab({ activeTabId: nextTabId }));
    // };

    const handleTabClose = (tab: ITab) => {
        // if (event) {
        //   event.stopPropagation();
        // }
        dispatch(setIsDetailViewOpen(false));
    };

    const handleCreateNewClick = () => {
        setSelectedItem(undefined);
        setClonedObject(undefined);
        const id = uuidv4();
        const documentDate = new Date().toLocaleString().slice(0, 9);
        const newTab: ITab = {
            id,
            title: `${entity.title} from ${documentDate}`,
            entity,
            side: "left",
            isModified: false,
            parentTabId: activeTabId,
            // isChild: true,
            // entity,
            // isActive: true,
            component: (
                <NewDocumentModalView
                    entity={entity}
                    onClose={(event) => {
                        handleCloseDetailView(true);
                    }}
                    // id={selectedItem.id}
                    clonedObject={clonedObject}
                />
            )
        };
        dispatch(addTab(newTab));
        // if (direction === "horizontal" && rightTabs.length === 0) {
        //   dispatch(addLeftTab(newTab));
        // } else {
        //   if (entity.pluralTitle === leftTabs[0]?.tabTitle) {
        //     if (direction === "vertical") {
        //       dispatch(addTopTab(newTab));
        //     } else if (direction === "horizontal") {
        //       dispatch(addLeftTab(newTab));
        //     }
        //   } else if (entity.pluralTitle === rightTabs[0]?.tabTitle) {
        //     if (direction === "vertical") {
        //       dispatch(addBottomTab(newTab));
        //     } else if (direction === "horizontal") {
        //       dispatch(addRightTab(newTab));
        //     }
        //   }
        // }
    };

    const handleCloneClick = async () => {
        const id = uuidv4();
        const dataService = entity.getDataService();
        const clonedObject = await dataService.Clone(selectedItem.id);
        setClonedObject(clonedObject);
        // setIsDetailViewOpen(true);
        dispatch(setIsDetailViewOpen(true));
        dataService.Get(selectedItem.id).then((itemNumber: any) => {
            const newTab: ITab = {
                id,
                title: "",
                entity,
                side: "left",
                isModified: false,
                // id,
                // entity,
                // parentId: activeTabId,
                // isChild: true,
                // isActive: true,
                documentNumber: itemNumber.documentNumber,
                documentDate: new Date(itemNumber.documentDate).toLocaleString().slice(0, 10),
                documentStatus: itemNumber.status,
                component: <NewDocumentModalView entity={entity} onClose={handleCloseDetailView} id={selectedItem.id}
                                                 clonedObject={clonedObject}/>
            };
            dispatch(addTab(newTab));
            // if (direction === "horizontal" && rightTabs.length === 0) {
            //   dispatch(addLeftTab(newTab));
            // } else {
            //   if (
            //     entity.pluralTitle === leftTabs[0]?.tabTitle &&
            //     direction === "vertical"
            //   ) {
            //     dispatch(addTopTab(newTab));
            //   }
            //   if (
            //     entity.pluralTitle === rightTabs[0]?.tabTitle &&
            //     direction === "vertical"
            //   ) {
            //     dispatch(addBottomTab(newTab));
            //   }

            //   if (
            //     entity.pluralTitle === leftTabs[0]?.tabTitle &&
            //     direction === "horizontal"
            //   ) {
            //     dispatch(addLeftTab(newTab));
            //   }
            //   if (
            //     entity.pluralTitle === rightTabs[0]?.tabTitle &&
            //     direction === "horizontal"
            //   ) {
            //     dispatch(addRightTab(newTab));
            //   }
            // }
        });
    };

    const handleCreateDocumentActionPopupButtonClick = () => {
        setCreateDocumentActionVisible(!createDocumentActionVisible);
    };

    const handleClick = () => {
        setCreateMoreActionsVisible(!createMoreActionsVisible);
    };

    const handleCreateDocumentAction = async (action: IListViewCreateDependedDocumentAction) => {
        const clonedObject = await action.createBaseAtFunction({
            id: selectedItem.id
        });
        const id = uuidv4();
        const newTab: ITab = {
            id,
            title: `${action.appEntity?.title} ${clonedObject.documentNumber} ${format(clonedObject.documentDate, "dd/MM/yyyy HH:mm")}`,
            side: "left",
            isModified: false,
            component: <NewDocumentModalView entity={action.appEntity} onClose={handleCloseDetailView}
                                             id={selectedItem.id} clonedObject={clonedObject}/>
        };
        dispatch(addTab(newTab));
        // dispatch(
        //   addLeftTab({
        //     id,
        //     entity: action.appEntity,
        //     isActive: true,
        //     documentNumber: clonedObject.documentNumber,
        //     documentDate: new Date(clonedObject.documentDate)
        //       .toLocaleString()
        //       .slice(0, 10),
        //     documentStatus: clonedObject.status,
        //     component: (
        //       <NewDocumentModalView
        //         entity={action.appEntity}
        //         onClose={handleCloseDetailView}
        //         id={selectedItem.id}
        //         clonedObject={clonedObject}
        //       />
        //     ),
        //     tabTitle: `${action.appEntity?.title} ${clonedObject.documentNumber} ${clonedObject.documentDate}`,
        //   })
        // );
        // dispatch(setIsDetailViewOpen(true));
        setCreateBaseAtDocumentEntity(action.appEntity);
        setIsDetailViewCreateBaseAtDocumentOpen(true);
        setCreateDocumentActionVisible(false);
        setCreateDocumentEntity(action.appEntity);
        // dispatch(setIsDetailViewOpen(true));
    };

    // useEffect(() => {
    //   if (isDetailViewCreateBaseAtDocumentOpen) {
    //     const id = uuidv4();
    //     const dataService = entity.getDataService();
    //     dataService.Get(selectedItem.id).then((itemNumber: any) => {
    //       console.log(createBaseAtDocumentEntity);
    //       dispatch(
    //         addLeftTab({
    //           id,
    //           entity,
    //           isActive: true,
    //           documentNumber: itemNumber.documentNumber,
    //           documentDate: new Date(itemNumber.documentDate)
    //             .toLocaleString()
    //             .slice(0, 10),
    //           documentStatus: itemNumber.status,
    //           component: (
    //             <NewDocumentModalView
    //               baseObject={baseObject}
    //               entity={createBaseAtDocumentEntity}
    //               onClose={handleCloseDetailView}
    //               id={selectedItem.id}
    //             />
    //           ),
    //           tabTitle: `${createBaseAtDocumentEntity?.title} ${itemNumber.documentNumber} ${itemNumber.documentDate}`,
    //         })
    //       );
    //     });
    //   }
    // }, [isDetailViewCreateBaseAtDocumentOpen, baseObject]);

    useEffect(() => {
        // console.log("useEffect gridUpdateTrigger" + gridUpdateTrigger);
        fetchData();
    }, [gridUpdateTrigger, searchText, startDate, endDate]);
    // const handleCreateDocumentAction = async (
    //   action: IListViewCreateDocumentAction
    // ) => {
    //   const dataService = entity.getDataService();
    //   const newDocument = await dataService.createBasedAtOrder(action.appEntity);
    //   console.log('newDocument', newDocument)
    //   setCreateDocumentEntity(newDocument);

    //   setCreateDocumentActionVisible(false);
    //   setIsDetailViewCreateDocumentOpen(true);
    // };

    const handleCloseDetailView = async (result: boolean) => {
        setIsDetailViewCreateBaseAtDocumentOpen(false);
        setBaseObject(null);
        if (result) {
            dispatch(setGridUpdateTrigger());
        }
    };

    const handleSearchTextChange = async (event: any) => {
        setSearchText(event.target.value);
    };

    const handleOpenDocumentTransactions = async () => {
        if (!selectedItem) {
            return;
        }
        // navigate(`/app/transactions/${selectedItemId}`);
        const id = uuidv4();
        const transactionService = new TransactionService();
        const documentId = selectedItem.id;
        const documentTransactions = await transactionService.getDocumentTransactions(documentId);
        const newTab: ITab = {
            id,
            title: `Transactions`,
            entity,
            side: "left",
            isModified: false,
            component: <TransactionsPage documentTransactions={documentTransactions}/>
        };
        dispatch(addTab(newTab));
        // if (direction === "horizontal" && rightTabs.length === 0) {
        //   dispatch(addLeftTab(newTab));
        // } else {
        //   if (
        //     entity.pluralTitle === leftTabs[0]?.tabTitle &&
        //     direction === "vertical"
        //   ) {
        //     dispatch(addTopTab(newTab));
        //   }
        //   if (
        //     entity.pluralTitle === rightTabs[0]?.tabTitle &&
        //     direction === "vertical"
        //   ) {
        //     dispatch(addBottomTab(newTab));
        //   }

        //   if (
        //     entity.pluralTitle === leftTabs[0]?.tabTitle &&
        //     direction === "horizontal"
        //   ) {
        //     dispatch(addLeftTab(newTab));
        //   }
        //   if (
        //     entity.pluralTitle === rightTabs[0]?.tabTitle &&
        //     direction === "horizontal"
        //   ) {
        //     dispatch(addRightTab(newTab));
        //   }
        // }
    };

    const handleOpenHierarchy = () => {
        if (!selectedItem) {
            return;
        }
        const id = uuidv4();
        const documentId = selectedItem.id;
        const newTab: ITab = {
            id,
            title: `Hierarchy`,
            entity,
            side: "left",
            isModified: false,
            component: <DocumentsHierarchyPage documentId={documentId} handleTabClose={handleTabClose}
                                               handleCloseDetailView={handleCloseDetailView}/>
        };
        dispatch(addTab(newTab));
        // if (direction === "horizontal" && rightTabs.length === 0) {
        //   dispatch(addLeftTab(newTab));
        // } else {
        //   if (
        //     entity.pluralTitle === leftTabs[0]?.tabTitle &&
        //     direction === "vertical"
        //   ) {
        //     dispatch(addTopTab(newTab));
        //   }
        //   if (
        //     entity.pluralTitle === rightTabs[0]?.tabTitle &&
        //     direction === "vertical"
        //   ) {
        //     dispatch(addBottomTab(newTab));
        //   }

        //   if (
        //     entity.pluralTitle === leftTabs[0]?.tabTitle &&
        //     direction === "horizontal"
        //   ) {
        //     dispatch(addLeftTab(newTab));
        //   }
        //   if (
        //     entity.pluralTitle === rightTabs[0]?.tabTitle &&
        //     direction === "horizontal"
        //   ) {
        //     dispatch(addRightTab(newTab));
        //   }
        // }
    };

    // More actions
    const handleOpenMoreActions = () => {
        if (!selectedItem) {
            return;
        }
        navigate("/app/more-actions");
    };

    //
    const handlePostDocumentClick = async () => {
        if (!selectedItem) {
            return;
        }
        const dataService = entity.getDataService();
        // await dataService.PostDocument(selectedItemId);
        try {
            setIsLoading(true);
            setIsFailure(false);
            setErrorMessage(null);
            const responseDto: IPostDocumentResponseDto = await dataService.PostDocument(selectedItem.id);
            if (responseDto.success) {
                toast("Posted successfully");
            } else {
                toast.error("Posting failed");
                responseDto.validationErrors.forEach((error: string) => {
                    dispatch(
                        addMessage({
                            message: error,
                            type: "error",
                            date: new Date(),
                            markedAsRead: false
                        })
                    );
                });
            }
            dispatch(setGridUpdateTrigger());
        } catch (e: any) {
            setIsFailure(true);
            setErrorMessage(e.message);
            toast.error(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearDates = () => {
        setStartDate(undefined);
        setEndDate(undefined);
        setShowDateRangeBox(false);
    };

    const handleCancelDocumentClick = async () => {
        if (!selectedItem) {
            return;
        }
        const dataService = entity.getDataService();
        try {
            setIsLoading(true);
            setIsFailure(false);
            setErrorMessage(null);
            await dataService.CancelDocument(selectedItem.id);
            dispatch(setGridUpdateTrigger());
            toast.success("Document cancelled successfully");
        } catch (e: any) {
            toast.error(e.message);
            setIsFailure(true);
            setErrorMessage(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectionChanged = useCallback((e: any) => {
        if (e.selectedRowKeys && e.selectedRowKeys.length > 0) {
            setSelectedItem(e.selectedRowKeys[0]);
        }
    }, []);

    const dataGridStatusColumnRender = useCallback((column: any) => {
        let statusName = "draft"; //cellElement.data.statusName;
        let statusColor = "black"; //cellElement.data.statusColor;
        switch (column.data.status) {
            case 0:
                statusName = t("Draft");
                statusColor = "black";
                break;
            case 1:
                statusName = t("Posted");
                statusColor = "green";
                break;
            case 2:
                statusName = t("Deleted");
                statusColor = "red";
                break;
            default:
                statusName = t("Draft");
                statusColor = "black";
                break;
        }
        return <div style={{color: statusColor}}>{statusName}</div>;
    }, []);

    const renderDataGrid = useCallback(
        (fields: IListViewField[]) => {
            return (
                <DataGrid
                    ref={dataGridRef}
                    dataSource={gridData}
                    sorting={{mode: "multiple"}}
                    allowColumnReordering={true}
                    rowAlternationEnabled={false}
                    showBorders={false}
                    showRowLines={true}
                    remoteOperations={{
                        groupPaging: listViewOptions?.paginationEnabled === true
                    }}
                    selection={{mode: "single"}}
                    //constant reload was caused by loadpanel enabled true and showIndicator true
                    loadPanel={{enabled: false, showIndicator: false, showPane: false}}
                    onSelectionChanged={handleSelectionChanged}
                >
                    <ColumnChooser enabled={true}/>
                    <GroupPanel visible={true}/>
                    <SearchPanel visible={true} highlightCaseSensitive={true}/>
                    <Grouping autoExpandAll={false}/>

                    <Column dataField="statusName" dataType="string" caption={"Status"} width={75}
                            cellRender={dataGridStatusColumnRender}/>

                    <Column dataField="documentDate" dataType="datetime" sortOrder={"asc"} sortIndex={0}
                            allowSorting={true}/>
                    <Column dataField="documentNumber" dataType="string" sortOrder={"asc"} sortIndex={1}/>

                    {fields.map((field, index) => (
                        <Column key={index} dataField={field.fieldName} dataType={field.fieldType} caption={field.title}
                                cellRender={field.cellRender}/>
                    ))}

                    <Pager allowedPageSizes={pageSizes} showPageSizeSelector={true}/>
                    <Paging defaultPageSize={10}/>
                </DataGrid>
            );
        },
        [gridData]
    );

    const isHorizontal = renderType === "horizontal";
    const isVertical = renderType === "vertical";
    const hasTabs = tabs.length > 0;
    //
    // let cardClass = "listview-card";
    // if (isHorizontal && hasTabs) {
    //     cardClass += " listview-card-horizontal";
    // } else if (isVertical && hasTabs) {
    //     cardClass += " listview-card-vertical";
    // }

    return (
        <>
            <div className="main-container container-fluid" id={"list-documents-view-port"}>
                <div className={"card card-body border"}>
                    <Row className={"row-sm"}>
                        <Col lg={12}>
                            <NewDocumentListViewToolbar
                                toolbarOptions={toolbarOptions}
                                setShowDateRangeBox={setShowDateRangeBox}
                                handleClick={handleClick}
                                handleOpenHierarchy={handleOpenHierarchy}
                                handleCreateNewClick={handleCreateNewClick}
                                handleSearchTextChange={handleSearchTextChange}
                                handleCloneClick={handleCloneClick}
                                handleOpenDocumentTransactions={handleOpenDocumentTransactions}
                                createMoreActionsVisible={createMoreActionsVisible}
                                setCreateMoreActionsVisible={setCreateMoreActionsVisible}
                                uniqueId={uniqueId}
                                selectedItem={selectedItem}
                                handleComponentRefresh={handleComponentRefresh}
                                createMoreActions={createMoreActions}
                                handleCancelDocumentClick={handleCancelDocumentClick}
                                handlePostDocumentClick={handlePostDocumentClick}
                                handleCreateDocumentAction={handleCreateDocumentAction}
                                handleEditClick={handleEditClick}
                                handleOpenMoreActions={handleOpenMoreActions}
                                handleDeleteClick={handleDeleteClick}
                                setCreateDocumentActionVisible={setCreateDocumentActionVisible}
                                createDocumentActionVisible={createDocumentActionVisible}
                                handleCreateDocumentActionPopupButtonClick={handleCreateDocumentActionPopupButtonClick}
                                createDocumentActions={createDocumentActions}
                            >
                                {children}
                            </NewDocumentListViewToolbar>
                        </Col>
                    </Row>
                    <Row className={"row-sm"} onDoubleClick={handleEditClick}>
                        <Col lg={12}>{renderDataGrid(fields)}</Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            {/*{isLoading && (<b>Loading...</b>)}*/}
                            {isFailure && <b>{errorMessage}</b>}
                        </Col>
                    </Row>
                </div>
                <NFLoadPanel isLoading={isLoading || isDetailLoading} message={t("Loading")}/>
                {/*<LoadPanel*/}
                {/*    shadingColor="rgba(0,0,0,0.1)"*/}
                {/*    visible={isLoading || isDetailLoading}*/}
                {/*    showIndicator={true}*/}
                {/*    showPane={true}*/}
                {/*    shading={true}*/}
                {/*    message={t("Loading")}*/}
                {/*/>*/}
            </div>
            <Popup
                visible={showDateRangeBox}
                onHidden={handleCloseDataRangeModal}
                container={"#list-documents-view-port"}
                title={"Select date range"}
                height={"auto"}
                width={"auto"}
                hideOnOutsideClick={false}
            >
                <DateRangeBox startDate={startDate} endDate={endDate} onStartDateChange={handleStartDateChange}
                              width={600} height={50} onEndDateChange={handleEndDateChange}/>

                <div className={"d-flex mt-2"}>
                    <span className="flex-grow-1"></span>
                    <Button className="btn btn-danger" onClick={handleClearDates}>
                        {t("Clear")}
                    </Button>
                    <span className="px-2"></span>
                    <Button className="btn btn-primary" onClick={RefreshGridData}>
                        {t("Apply")}
                    </Button>
                    <span className="px-2"></span>
                    <Button className="btn btn-light" onClick={handleCloseDataRangeModal}>
                        {t("Close")}
                    </Button>
                </div>
            </Popup>
        </>
    );
};
